import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Collapse, Form, Table } from "react-bootstrap";
import { TiMinus, TiPlus } from "react-icons/ti";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import {
  getAuditoriaAdministracion,
  getAuditoriaCliente,
  getAuditoriaCuenta,
} from "../../../api/auditoriaApi";
import "./AuditHistoryPanel.css";

const LABELS = {
  gastos: "Gastos",
  cierres: "Cierres",
  anticipos: "Anticipos",
  asistencias: "Asistencias",
  facturas: "Facturas / pagos profesionales",
  marcaciones: "Marcaciones",
  pagosProveedores: "Pagos a proveedores",
  ordenesPago: "Órdenes de pago",
  pacientes: "Pacientes",
};

const DATASETS = {
  cliente: ["gastos", "cierres", "anticipos", "asistencias"],
  cuenta: ["facturas", "gastos", "asistencias", "cierres", "marcaciones"],
  administracion: ["gastos", "cierres", "pagosProveedores", "ordenesPago", "pacientes"],
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const renderCell = (column, value) => {
  const formatted = formatValue(value);
  if (column.toLocaleLowerCase("es") === "idliquidacionclientes" && value !== null && value !== undefined && value !== "") {
    return <Link to={`/fichaliquidacion/${value}`}>{formatted}</Link>;
  }
  if (column.toLocaleLowerCase("es") === "idpacientes" && value !== null && value !== undefined && value !== "") {
    return <Link to={`/Fichaclientes/${value}`}>{formatted}</Link>;
  }
  return formatted;
};

function AuditTable({ name, rows }) {
  const [filter, setFilter] = useState("");
  const columns = useMemo(
    () => Array.from(new Set((rows || []).flatMap((row) => Object.keys(row || {})))),
    [rows]
  );
  const filtered = useMemo(() => {
    const term = filter.trim().toLocaleLowerCase("es");
    if (!term) return rows || [];
    return (rows || []).filter((row) =>
      Object.values(row || {}).some((value) => formatValue(value).toLocaleLowerCase("es").includes(term))
    );
  }, [filter, rows]);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(rows || []);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, LABELS[name] || name);
    XLSX.writeFile(workbook, `auditoria_${name}.xlsx`);
  };

  return (
    <div className="audit-table-block">
      <div className="audit-table-toolbar">
        <h6 className="mb-0">{LABELS[name] || name} <span className="badge bg-secondary">{filtered.length}</span></h6>
        {name === "pacientes" && Boolean(rows?.length) && (
          <Button size="sm" variant="success" onClick={exportToExcel}>
            Descargar Excel
          </Button>
        )}
        <Form.Control
          size="sm"
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder={`Filtrar ${LABELS[name] || name}...`}
          aria-label={`Filtrar ${LABELS[name] || name}`}
        />
      </div>
      {!rows?.length ? (
        <Alert variant="light" className="border mb-0">Sin datos en el rango seleccionado.</Alert>
      ) : !filtered.length ? (
        <Alert variant="warning" className="mb-0">Ningún registro coincide con el filtro.</Alert>
      ) : (
        <div className="audit-table-scroll">
          <Table responsive striped size="sm" className="primary-table-bordered mb-0 audit-table">
            <thead className="thead-primary"><tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
            <tbody>
              {filtered.map((row, rowIndex) => (
                <tr key={`${name}-${rowIndex}`}>
                  {columns.map((column) => <td key={column}>{renderCell(column, row[column])}</td>)}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function AuditHistoryPanel({ scope, entityId, title = "Consultar movimientos", dataset }) {
  const [open, setOpen] = useState(false);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState("");
  const datasets = dataset ? [dataset] : (DATASETS[scope] || []);
  const allowsEmptyRange = datasets.length === 1 && datasets[0] === "pacientes";

  const consultar = async (tipo) => {
    if ((!desde || !hasta) && !allowsEmptyRange) {
      Swal.fire("Rango requerido", "Seleccione la fecha desde y hasta antes de traer los datos.", "warning");
      return;
    }
    if ((desde && !hasta) || (!desde && hasta)) {
      Swal.fire("Rango incompleto", "Seleccione ambas fechas o deje las dos vacías para traer todos los pacientes.", "warning");
      return;
    }
    if (desde > hasta) {
      Swal.fire("Rango inválido", "La fecha desde no puede ser posterior a la fecha hasta.", "warning");
      return;
    }
    setLoading(tipo);
    try {
      const rango = { desde, hasta, tipo };
      const result = scope === "cliente"
        ? await getAuditoriaCliente(entityId, rango)
        : scope === "cuenta"
          ? await getAuditoriaCuenta(entityId, rango)
          : await getAuditoriaAdministracion(rango);
      setData((current) => ({ ...current, ...result }));
    } catch (error) {
      Swal.fire("Error", error.message || "No se pudieron traer los datos.", "error");
    } finally {
      setLoading("");
    }
  };

  return (
    <Card className="shadow-sm audit-panel">
      <Card.Header
        className="collapsible-header audit-main-header"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen((value) => !value);
          }
        }}
      >
        <h5 className="mb-0">{title}</h5>
        <span className="collapsible-toggle" aria-hidden="true">
          {open ? <TiMinus /> : <TiPlus />}
        </span>
      </Card.Header>
      <Collapse in={open}>
        <div>
          <Card.Body>
            <div className="audit-range">
              <Form.Group>
                <Form.Label>Desde</Form.Label>
                <Form.Control type="date" value={desde} onChange={(event) => { setDesde(event.target.value); setData({}); }} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Hasta</Form.Label>
                <Form.Control type="date" value={hasta} min={desde || undefined} onChange={(event) => { setHasta(event.target.value); setData({}); }} />
              </Form.Group>
            </div>
            <Alert variant="info" className="mt-3 mb-0">
              {allowsEmptyRange
                ? "Deje ambas fechas vacías para traer todos los pacientes, o seleccione un rango para filtrar sus movimientos."
                : "Seleccione el rango y luego traiga solamente la tabla que desea consultar."}
            </Alert>
            <div className="audit-tables">
              {datasets.map((name) => (
                <Card key={name} className="audit-dataset-card">
                  <Card.Header className="audit-section-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">{LABELS[name] || name}</h6>
                    <Button
                      size="sm"
                      onClick={() => consultar(name)}
                      disabled={Boolean(loading) || (!allowsEmptyRange && (!desde || !hasta))}
                    >
                      {loading === name ? "Trayendo…" : `Traer ${LABELS[name] || name}`}
                    </Button>
                  </Card.Header>
                  {Object.prototype.hasOwnProperty.call(data, name) && (
                    <Card.Body><AuditTable name={name} rows={data[name]} /></Card.Body>
                  )}
                </Card>
              ))}
            </div>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
}
